import { NgTemplateOutlet } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { AdminAnnouncerService } from '../a11y/admin-announcer.service';
import { AdminLoadFailedComponent } from '../load-failed/admin-load-failed.component';
import { ADMIN_TABLE, AdminSortOption, AdminTableSortHost } from './admin-table.token';
import { AdminPaginatorComponent } from './paginator.component';
import { AdminTableIconComponent } from './table-icon.component';
import {
  AdminTableEmptyDirective,
  AdminTableHeaderDirective,
  AdminTableRowDirective,
} from './table-templates.directive';
import { clampPage, pageSlice, SortDir, sortRows, TablePage, TableSort } from './table.util';

/** Table roles written out, so they survive the phone layout's display changes. */
function setRoles(table: HTMLTableElement): void {
  const set = (el: Element, role: string) => {
    if (el.getAttribute('role') !== role) el.setAttribute('role', role);
  };
  set(table, 'table');
  table.querySelectorAll(':scope > thead, :scope > tbody').forEach((g) => set(g, 'rowgroup'));
  table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr').forEach((r) => set(r, 'row'));
  table.querySelectorAll(':scope > thead > tr > th').forEach((c) => set(c, 'columnheader'));
  table.querySelectorAll(':scope > tbody > tr > td').forEach((c) => set(c, 'cell'));
}

/**
 * Admin data table (replaces PrimeNG's p-table). Two modes:
 * - client (default): sorts and pages `rows` itself;
 * - `lazy`: shows `rows` as given (one server page) and emits `pageChange`
 *   when the user pages; the parent fetches and passes `page` / `total` back.
 * `pageChange` fires only on user action, never on init, so lazy parents load
 * their first page themselves. Header cells sort with `th[adminSort]`: client
 * mode sorts `rows`; lazy mode emits `sortChange` (back to page 1) for the parent to refetch.
 *
 * While the first load runs (`loading` with no rows yet) the body shows
 * placeholder rows rather than the empty text; when `failed` is set it shows
 * "Couldn't load the {itemLabel}" with Try again (`retry`) instead.
 *
 * After a reload (a filter, sort or page change; not the page's first load, which
 * the page heading announces) screen readers hear the result, e.g. "Payout requests: 12".
 *
 * On phones each row is shown as a stacked card; every cell is labelled with
 * its column's header text (`data-label`, copied from the header row), so
 * the pages' row templates need nothing extra.
 */
@Component({
  selector: 'admin-table',
  standalone: true,
  imports: [AdminLoadFailedComponent, AdminPaginatorComponent, AdminTableIconComponent, NgTemplateOutlet],
  providers: [{ provide: ADMIN_TABLE, useExisting: forwardRef(() => AdminTableComponent) }],
  // Unencapsulated: the header/row templates belong to the parent component's view.
  encapsulation: ViewEncapsulation.None,
  styleUrls: ['./admin-table.component.scss'],
  template: `
    <div class="admin-table" [class.admin-table--hover]="rowHover">
      <div class="admin-table__scroll">
        <table [attr.aria-busy]="loading || null">
          @if (caption) {
            <caption class="admin-visually-hidden">
              {{
                caption
              }}
            </caption>
          }
          <thead>
            @if (header) {
              <ng-container *ngTemplateOutlet="header.template"></ng-container>
            }
          </thead>
          <tbody>
            @if (failed) {
              <tr class="admin-table__state">
                <td colspan="100">
                  <admin-load-failed [what]="itemLabel || 'list'" (retry)="retry.emit()"></admin-load-failed>
                </td>
              </tr>
            } @else if (loading && !view.length) {
              @for (n of placeholders; track n) {
                <tr class="admin-table__placeholder" aria-hidden="true">
                  <td colspan="100"><span class="row-placeholder"></span></td>
                </tr>
              }
            } @else {
              @for (row of view; track rowKey(row, $index)) {
                @if (body) {
                  <ng-container
                    *ngTemplateOutlet="body.template; context: { $implicit: row, index: $index }"
                  ></ng-container>
                }
              } @empty {
                @if (empty) {
                  <ng-container *ngTemplateOutlet="empty.template"></ng-container>
                }
              }
            }
          </tbody>
        </table>
      </div>
      <!-- Phones hide the header row, so its sortable columns are offered here (CSS shows it above the rows). -->
      @if (sortOptions.length) {
        <div class="admin-table__phone-sort">
          <label>
            Sort by
            <select (change)="sortTo($any($event.target).value, 'asc')">
              @if (!sortField) {
                <option value="" selected disabled>Choose</option>
              }
              @for (o of sortOptions; track o.field) {
                <option [value]="o.field" [selected]="o.field === sortField">{{ o.label }}</option>
              }
            </select>
          </label>
          <button
            type="button"
            [disabled]="!sortField"
            (click)="sortTo(sortField ?? '', sortDir === 'asc' ? 'desc' : 'asc')"
          >
            {{ sortDir === 'asc' ? 'Ascending' : 'Descending' }}
          </button>
        </div>
      }
      @if (paginator && !failed && !(loading && !view.length)) {
        <admin-paginator
          [page]="currentPage"
          [pageSize]="currentSize"
          [total]="totalRecords"
          [pageSizeOptions]="pageSizeOptions"
          [itemLabel]="itemLabel"
          (pageChange)="onPage($event)"
        ></admin-paginator>
      }
      @if (loading && view.length && !failed) {
        <div class="admin-table__mask" role="status">
          <admin-table-icon class="admin-table__spinner" name="spinner" aria-hidden="true"></admin-table-icon>
          <span class="admin-visually-hidden">Loading {{ itemLabel || 'rows' }}…</span>
        </div>
      }
    </div>
  `,
})
export class AdminTableComponent<T = unknown> implements OnChanges, AfterViewChecked, AdminTableSortHost {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly announcer = inject(AdminAnnouncerService);
  /** Set once the first load has finished; later loads are announced. */
  private loadedOnce = false;

  @Input() rows: readonly T[] | null | undefined = [];
  @Input() loading = false;
  @Input() paginator = true;
  @Input() pageSize = 50;
  @Input() pageSizeOptions: number[] = [];
  /** Noun for the "Showing x to y of n …" line; omitted when empty. */
  @Input() itemLabel = '';
  /** What the table lists, for screen readers (e.g. "Ledger entries"); not shown. */
  @Input() caption = '';
  @Input() rowHover = false;
  @Input() sortField?: string;
  @Input() sortDir: SortDir = 'asc';
  /** Server paging: `rows` is one page; `total` and `page` (1-based) come from the parent. */
  @Input() lazy = false;
  @Input() total = 0;
  @Input() page = 1;
  /** The load failed: show "Couldn't load…" with Try again instead of rows or the empty text. */
  @Input() failed = false;
  @Output() readonly pageChange = new EventEmitter<TablePage>();
  /** Lazy mode: a header was clicked; the parent fetches page 1 sorted this way. */
  @Output() readonly sortChange = new EventEmitter<TableSort>();
  /** Try again on the failed row. */
  @Output() readonly retry = new EventEmitter<void>();

  readonly placeholders = [1, 2, 3, 4, 5];

  @ContentChild(AdminTableHeaderDirective) header?: AdminTableHeaderDirective;
  @ContentChild(AdminTableRowDirective) body?: AdminTableRowDirective<T>;
  @ContentChild(AdminTableEmptyDirective) empty?: AdminTableEmptyDirective;

  currentPage = 1;
  currentSize = 50;
  view: T[] = [];
  /** The header's sortable columns, for the phone layout's "Sort by". */
  sortOptions: AdminSortOption[] = [];
  /** The body may have new rows or cells to label; cleared once labelled. */
  private relabel = true;

  /** Keeps a row's DOM across sorts and reloads: its id when it has one, else its position. */
  rowKey(row: T, index: number): unknown {
    const id = (row as { id?: unknown } | null)?.id;
    return id ?? `#${index}`;
  }

  get totalRecords(): number {
    return this.lazy ? this.total : this.safeRows.length;
  }

  /** Anything that isn't an array (a bad API response, null) renders as no rows. */
  private get safeRows(): readonly T[] {
    return Array.isArray(this.rows) ? this.rows : [];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page']) this.currentPage = this.page;
    if (changes['pageSize']) this.currentSize = this.pageSize;
    this.refresh();
    const loading = changes['loading'];
    if (loading && loading.previousValue && !loading.currentValue) {
      if (this.loadedOnce) this.announcer.announce(this.resultText());
      this.loadedOnce = true;
    }
  }

  /** What a finished load found, for screen readers: "Reservations: 12", "Reservations: none". */
  resultText(): string {
    const label = this.itemLabel || 'rows';
    const name = label.charAt(0).toUpperCase() + label.slice(1);
    if (this.failed) return `Couldn't load the ${label}`;
    const n = this.totalRecords;
    return `${name}: ${n ? n.toLocaleString() : 'none'}`;
  }

  // Headers register while the table renders, so the list changes just after, not mid-check.
  registerSort(option: AdminSortOption): void {
    this.updateSortOptions((options) => [...options, option]);
  }

  unregisterSort(option: AdminSortOption): void {
    this.updateSortOptions((options) => options.filter((o) => o !== option));
  }

  private updateSortOptions(change: (options: AdminSortOption[]) => AdminSortOption[]): void {
    void Promise.resolve().then(() => {
      // In column order, so a header that comes back (shown again by an @if) keeps its place.
      this.sortOptions = change(this.sortOptions).sort((a, b) =>
        a.element.compareDocumentPosition(b.element) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1,
      );
      // The header row changed: relabel the phone cards and their roles too.
      this.relabel = true;
      this.cdr.markForCheck();
    });
  }

  /** Label each body cell with its column header, for the stacked phone layout,
   *  and spell out the table roles, which browsers drop once the phone layout
   *  restyles the rows as cards. Only after the rows or the loading state
   *  changed, not on every check. */
  ngAfterViewChecked(): void {
    if (!this.relabel) return;
    this.relabel = false;
    const table = this.host.nativeElement.querySelector('table');
    if (!table) return;
    setRoles(table);
    const labels = Array.from(table.querySelectorAll('thead th')).map((th) => th.textContent?.trim() ?? '');
    if (!labels.length) return;
    for (const row of Array.from(table.querySelectorAll('tbody > tr'))) {
      Array.from(row.children).forEach((cell, i) => {
        const label = (cell as HTMLElement).hasAttribute('colspan') ? '' : labels[i] ?? '';
        if (cell.getAttribute('data-label') !== label) cell.setAttribute('data-label', label);
      });
    }
  }

  /** Sorts by `field` in `dir` (the phone layout's "Sort by"); the headers toggle with sortBy(). */
  sortTo(field: string, dir: SortDir): void {
    if (!field) return;
    this.sortField = field;
    this.sortDir = dir;
    if (this.lazy) {
      this.currentPage = 1;
      this.sortChange.emit({ field, dir });
      return;
    }
    this.refresh();
  }

  sortBy(field: string): void {
    this.sortDir = this.sortField === field && this.sortDir === 'asc' ? 'desc' : 'asc';
    this.sortField = field;
    if (this.lazy) {
      this.currentPage = 1;
      this.sortChange.emit({ field, dir: this.sortDir });
      return;
    }
    this.refresh();
  }

  onPage(event: TablePage): void {
    this.currentPage = event.page;
    this.currentSize = event.pageSize;
    if (!this.lazy) this.refresh();
    this.pageChange.emit(event);
  }

  private refresh(): void {
    this.relabel = true;
    const rows = this.safeRows;
    if (this.lazy) {
      // One server page; like PrimeNG, never render more than a page even if the server sends more.
      this.view = this.paginator ? rows.slice(0, this.currentSize) : [...rows];
      return;
    }
    this.currentPage = clampPage(this.currentPage, rows.length, this.currentSize);
    const sorted = sortRows(rows, this.sortField, this.sortDir);
    this.view = this.paginator ? pageSlice(sorted, this.currentPage, this.currentSize) : sorted;
  }
}
